import React, { useState } from "react";
import { getAsrSalesSummary } from "../../services/api";
import DatePickerWithValidation from "../ui/DatePickerWithValidation";
import { toDDMMYYYY } from "../../utils/dateUtils";

// ── Reference maps ────────────────────────────────────────────────────────────

const DOC_TYPE_LABELS: Record<string, string> = {
  TKT: "Ticket", MCO: "Misc Charges", EMD: "Elec Misc Doc",
  MSR: "Misc Sales Receipt", RFD: "Refund", BAG: "Excess Baggage",
  PTA: "Prepaid Ticket", TUR: "Tour Order", VCH: "Voucher",
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  A:  { label: "Active",     color: "bg-green-100 text-green-700" },
  E:  { label: "Exchanged",  color: "bg-blue-100 text-blue-700" },
  RF: { label: "Refunded",   color: "bg-yellow-100 text-yellow-700" },
  RV: { label: "Reversed",   color: "bg-orange-100 text-orange-700" },
  V:  { label: "Void",       color: "bg-red-100 text-red-700" },
};

const FOP_LABELS: Record<string, string> = {
  CA: "Cash", CC: "Credit Card", CK: "Check", ET: "Exchange Ticket",
  PT: "Pre-Paid", TV: "Travel Voucher", BT: "Travel Bank",
  GR: "Govt Bill", IN: "Involuntary", CT: "Certificate", XX: "Other",
};

// ── Types ─────────────────────────────────────────────────────────────────────

type FopDetail = {
  fopCode: string;
  currencyCode: string;
  fareAmount: number;
  taxAmount: number;
  totalAmount: number;
};

type Transaction = {
  documentType: string;
  documentNumber: string;
  passengerName: string;
  pnrLocator: string;
  ticketPrinterLniata: string;
  transactionTime: string;
  isElectronicTicket: boolean;
  isException: boolean;
  isDecouple: boolean;
  ticketStatusCode: string;
  totalCommissionAmount: number;
  fopDetails: FopDetail[];
};

type DailySubTotal = { fopCode: string; amount: number };
type DailyTotal = { currencyCode: string; subTotals: DailySubTotal[] };

type SalesSummaryData = {
  success: boolean;
  reportDate: string;
  stationNumber: string;
  employeeNumber: string;
  issueCity?: string;
  totalTransactions: number;
  transactions: Transaction[];
  dailyTotals: DailyTotal[];
  error?: string;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function todayISO() { return new Date().toISOString().split("T")[0]; }

function splitCodes(val: string): string[] | undefined {
  const arr = val.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);
  return arr.length > 0 ? arr : undefined;
}

function fmtAmt(n: number) {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Component ─────────────────────────────────────────────────────────────────

const AsrSalesSummary: React.FC = () => {
  const [ticketingProvider, setTicketingProvider] = useState("");
  const [stationNumber, setStationNumber]         = useState("");
  const [employeeNumber, setEmployeeNumber]       = useState("");
  const [reportDate, setReportDate]               = useState(todayISO());
  const [currencyCodes, setCurrencyCodes]         = useState("");
  const [fopCodes, setFopCodes]                   = useState("");
  const [documentTypes, setDocumentTypes]         = useState("");

  const [loading, setLoading]     = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [report, setReport]       = useState<SalesSummaryData | null>(null);
  const [docFilter, setDocFilter] = useState("ALL");

  const canFetch = ticketingProvider.trim() && stationNumber.trim() && employeeNumber.trim() && reportDate;

  const handleFetch = async () => {
    if (!canFetch) return;
    setLoading(true);
    setFetchError(null);
    setReport(null);
    setDocFilter("ALL");
    try {
      const data = await getAsrSalesSummary({
        ticketingProvider: ticketingProvider.trim().toUpperCase(),
        stationNumber: stationNumber.trim(),
        employeeNumber: employeeNumber.trim(),
        reportDate,
        currencyCodes: splitCodes(currencyCodes),
        fopCodes: splitCodes(fopCodes),
        documentTypes: splitCodes(documentTypes),
      });
      if (!data.success) {
        setFetchError(data.error ?? "Unknown error from server.");
      } else {
        setReport(data as SalesSummaryData);
      }
    } catch (err: any) {
      setFetchError(
        err?.response?.data?.errors?.[0]?.message ??
          err?.response?.data?.error ??
          err?.message ??
          "Failed to fetch report."
      );
    } finally {
      setLoading(false);
    }
  };

  const availableDocTypes = report
    ? Array.from(new Set(report.transactions.map((t) => t.documentType))).sort()
    : [];

  const filtered = report?.transactions.filter(
    (t) => docFilter === "ALL" || t.documentType === docFilter
  ) ?? [];

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-heading">ASR: Sales Summary</h1>
        <p className="text-sm text-gray-500 mt-1">
          Per-agent and per-station sales totals via Sabre TKT_AsrServicesRQ SalesSummaryRQ.
          Requires SabreSonic airline access.
        </p>
      </div>

      {/* Form */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label="Ticketing Provider *">
            <input value={ticketingProvider} onChange={(e) => setTicketingProvider(e.target.value.toUpperCase())}
              placeholder="e.g. 7F" maxLength={2}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-heading w-full focus:outline-none focus:ring-2 focus:ring-primary" />
          </Field>
          <Field label="Station Number *">
            <input value={stationNumber} onChange={(e) => setStationNumber(e.target.value)}
              placeholder="8-digit station" maxLength={10}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-heading w-full focus:outline-none focus:ring-2 focus:ring-primary" />
          </Field>
          <Field label="Employee Number *">
            <input value={employeeNumber} onChange={(e) => setEmployeeNumber(e.target.value)}
              placeholder="6-digit employee" maxLength={10}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-heading w-full focus:outline-none focus:ring-2 focus:ring-primary" />
          </Field>
          <Field label="Report Date *">
            <DatePickerWithValidation
              selected={reportDate}
              onSelect={setReportDate}
              maxDate={todayISO()}
              placeholder="dd/mm/yyyy"
            />
          </Field>
          <Field label="Currency Codes" hint="comma-separated, e.g. AED,USD">
            <input value={currencyCodes} onChange={(e) => setCurrencyCodes(e.target.value.toUpperCase())}
              placeholder="AED, USD" className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-heading w-full focus:outline-none focus:ring-2 focus:ring-primary" />
          </Field>
          <Field label="FOP Codes" hint="comma-separated, e.g. CA,CC">
            <input value={fopCodes} onChange={(e) => setFopCodes(e.target.value.toUpperCase())}
              placeholder="CA, CC" className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-heading w-full focus:outline-none focus:ring-2 focus:ring-primary" />
          </Field>
          <Field label="Document Types" hint="comma-separated, e.g. TKT,RFD">
            <input value={documentTypes} onChange={(e) => setDocumentTypes(e.target.value.toUpperCase())}
              placeholder="TKT, RFD" className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-heading w-full focus:outline-none focus:ring-2 focus:ring-primary" />
          </Field>
        </div>
        <div className="mt-4">
          <button
            onClick={handleFetch}
            disabled={loading || !canFetch}
            className="bg-primary text-white font-semibold px-6 py-2 rounded-full hover:bg-blue-800 transition text-sm disabled:opacity-50"
          >
            {loading ? "Loading…" : "Fetch Report"}
          </button>
        </div>
      </div>

      {fetchError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-6 text-sm">
          {fetchError}
        </div>
      )}

      {report && (
        <>
          {/* Meta */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 flex flex-wrap gap-6 text-sm">
            <MetaItem label="Report Date" value={toDDMMYYYY(report.reportDate)} />
            <MetaItem label="Station" value={report.stationNumber} />
            <MetaItem label="Employee" value={report.employeeNumber} />
            {report.issueCity && <MetaItem label="Issue City" value={report.issueCity} />}
            <MetaItem label="Transactions" value={String(report.totalTransactions)} bold />
          </div>

          {/* Daily totals */}
          {report.dailyTotals?.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
              <h3 className="text-xs uppercase font-bold text-gray-500 tracking-widest mb-3">Daily Totals</h3>
              <div className="flex flex-wrap gap-4">
                {report.dailyTotals.map((dt) => (
                  <div key={dt.currencyCode} className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 min-w-[160px]">
                    <p className="text-xs font-bold text-blue-500 mb-2">{dt.currencyCode}</p>
                    {dt.subTotals.map((st) => (
                      <div key={st.fopCode} className="flex justify-between gap-3 text-sm">
                        <span className="text-gray-500 text-xs">{FOP_LABELS[st.fopCode] ?? st.fopCode}</span>
                        <span className="font-semibold text-heading">{fmtAmt(st.amount)}</span>
                      </div>
                    ))}
                    <div className="border-t border-blue-200 mt-1.5 pt-1.5 flex justify-between text-sm">
                      <span className="text-gray-500 text-xs font-semibold">Total</span>
                      <span className="font-bold text-heading">
                        {fmtAmt(dt.subTotals.reduce((s, x) => s + x.amount, 0))}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Doc type filter */}
          {report.transactions.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              <FilterPill label={`All (${report.transactions.length})`} active={docFilter === "ALL"} onClick={() => setDocFilter("ALL")} />
              {availableDocTypes.map((dt) => (
                <FilterPill
                  key={dt}
                  label={`${dt} (${report.transactions.filter((t) => t.documentType === dt).length})`}
                  active={docFilter === dt}
                  onClick={() => setDocFilter(dt)}
                />
              ))}
            </div>
          )}

          {report.transactions.length === 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-400 text-sm">
              No transactions found for this date.
            </div>
          )}

          {/* Transactions table */}
          {filtered.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      {["Time","Passenger","PNR","Document #","Type","Status","Fare","Taxes","Total","FOP"].map((h) => (
                        <th key={h} className={`px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap ${h === "Fare" || h === "Taxes" || h === "Total" ? "text-right" : "text-left"}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((tx, idx) => {
                      const statusCfg = STATUS_CONFIG[tx.ticketStatusCode] ?? { label: tx.ticketStatusCode, color: "bg-gray-100 text-gray-600" };
                      const fop = tx.fopDetails?.[0];
                      return (
                        <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition">
                          <td className="px-4 py-3 font-mono text-xs text-gray-600 whitespace-nowrap">{tx.transactionTime ?? "—"}</td>
                          <td className="px-4 py-3 font-semibold text-heading whitespace-nowrap">{tx.passengerName || "—"}</td>
                          <td className="px-4 py-3 font-mono text-xs font-semibold text-heading whitespace-nowrap">{tx.pnrLocator || "—"}</td>
                          <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{tx.documentNumber || "—"}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="bg-gray-100 text-gray-700 text-xs font-semibold rounded px-2 py-0.5">{tx.documentType}</span>
                            {DOC_TYPE_LABELS[tx.documentType] && (
                              <span className="ml-1 text-gray-400 text-xs hidden lg:inline">{DOC_TYPE_LABELS[tx.documentType]}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`text-xs font-semibold rounded px-2 py-0.5 ${statusCfg.color}`}>{statusCfg.label}</span>
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-xs whitespace-nowrap">{fop ? fmtAmt(fop.fareAmount) : "—"}</td>
                          <td className="px-4 py-3 text-right font-mono text-xs whitespace-nowrap">{fop ? fmtAmt(fop.taxAmount) : "—"}</td>
                          <td className="px-4 py-3 text-right font-semibold text-heading whitespace-nowrap">
                            {fop ? `${fop.currencyCode} ${fmtAmt(fop.totalAmount)}` : "—"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {fop ? (
                              <span className="bg-gray-100 text-gray-700 text-xs font-semibold rounded px-2 py-0.5" title={FOP_LABELS[fop.fopCode] ?? fop.fopCode}>
                                {fop.fopCode}
                              </span>
                            ) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── Small shared sub-components ───────────────────────────────────────────────

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
        {label}
        {hint && <span className="font-normal normal-case text-gray-400 ml-1">({hint})</span>}
      </label>
      {children}
    </div>
  );
}

function MetaItem({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div>
      <span className="text-gray-500 text-xs uppercase font-semibold">{label}</span>
      <p className={`${bold ? "font-bold text-base" : "font-semibold"} text-heading`}>{value || "—"}</p>
    </div>
  );
}

function FilterPill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition ${
        active ? "bg-primary text-white border-primary" : "bg-white text-gray-600 border-gray-300 hover:border-primary hover:text-primary"
      }`}
    >
      {label}
    </button>
  );
}

export default AsrSalesSummary;
